import React, { useEffect, useState } from 'react';
import { Grid2 as Grid, Button, Tooltip, IconButton, Box, useMediaQuery, CircularProgress } from '@mui/material';
import {
  Actions,
  useDispatchProvider,
  useStateFormOrder,
  useStateFormOrderCustomer,
  useStateUser,
} from '../utils/StateProvider';
import { useForm } from 'react-hook-form';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  query,
  updateDoc,
  getDocs,
  where,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import {
  formatDateTime,
  formatDate,
  getNewDateTime,
  parseDateTime,
  renderValueOrderStatusChip,
  getNewDate,
  parseDate,
} from '../../utils/utils';
import { Add, Delete, Edit, Save, Undo, PersonAddAlt1, Phone } from '@mui/icons-material';
import { auth } from '../../config/firebase';
import SkeletonForm from '../utils/SkeletonForm';
import FieldText from '../fields/FieldText';
import FieldTextarea from '../fields/FieldTextArea';
import FieldCurrency from '../fields/FieldCurrency';
import FieldCustomerAutocomplete from '../fields/FieldCustomerAutocomplete';
import { createPortal } from 'react-dom';
import { usePermissions } from '../utils/AclProvider';
import FieldDateTime from '../fields/FieldDateTime';
import FieldSelect from '../fields/FieldSelect';
import { orderStatuses } from '../../data/statuses';
import FieldDate from '../fields/FieldDate';

const FormOrder = ({ portalRef = null }) => {
  const [customerStatus, setCustomerStatus] = useState(null);
  const [orderTransaction, setOrderTransaction] = useState(false);
  const [customerStatusLoading, setCustomerStatusLoading] = useState(false);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const permissions = usePermissions();
  const stateUser = useStateUser();
  const stateFormOrder = useStateFormOrder();
  const stateFormOrderCustomer = useStateFormOrderCustomer();
  const dispatch = useDispatchProvider();
  const { control, getValues, watch, reset, setValue } = useForm({
    defaultValues: {
      customerRef: '',
      downPayment: '0',
      total: '0',
      products: '0',
      orderStatus: 'new',
      comments: '',
      dateCreated: formatDateTime(getNewDateTime()),
      dateUpdated: '',
      dateShipping: '',
      dateCompleted: '',
      userCreated: '',
      userUpdated: '',
      userShipping: '',
      userCompleted: '',
    },
  });

  const [stateForm, setStateForm] = useState('create');
  const [stateLoading, setStateLoading] = useState(true);
  const [stateSaving, setStateSaving] = useState(false);
  const [stateCustomerPhone, setStateCustomerPhone] = useState(false);

  const orderDocRef = stateFormOrder ? doc(db, 'orders', stateFormOrder) : null;
  const ordersDocRef = collection(db, 'orders');

  const watchOrderStatus = watch('orderStatus');
  const watchOrderCustomer = watch('customerRef');

  const fetchOrder = async () => {
    if (stateFormOrder === '') {
      reset();
      setStateForm('create');
      return;
    }

    try {
      const docSnap = await getDoc(orderDocRef);
      if (docSnap.exists()) {
        setStateForm('edit');
        // console.log('edit', docSnap.data());
        // setValue(docSnap.data());
        const orderData = docSnap.data();
        // const customerSnap = await getDoc(orderData.customerRef);
        // setValue('customerRef', customerSnap.id);
        setValue('customerRef', orderData.customerRef?.id);
        setValue('orderStatus', orderData.orderStatus);
        setValue('comments', orderData.comments);
        setValue('products', orderData.products);
        setValue('total', orderData.total);
        setValue('downPayment', orderData.downPayment);
        setValue('dateCreated', formatDateTime(orderData.dateCreated));
        setValue('dateUpdated', formatDateTime(orderData.dateUpdated));
        setValue('dateShipping', formatDate(orderData.dateShipping));
        setValue('dateCompleted', formatDateTime(orderData.dateCompleted));
        setValue('userCreated', orderData.userCreated);
        setValue('userUpdated', orderData.userUpdated);
        setValue('userCompleted', orderData.userCompleted);
        setValue('userShipping', orderData.userShipping);
        setStateLoading(false);
      } else {
        dispatch &&
          dispatch({
            type: Actions.Snackbar,
            payload: { open: true, severity: 'error', message: `No user found!` },
          });
      }
    } catch (err) {
      dispatch &&
        dispatch({
          type: Actions.Snackbar,
          payload: { open: true, severity: 'error', message: `Error fetching document: ${err.message}` },
        });
    }
  };

  const addOrder = async () => {
    try {
      setStateSaving(true);
      const orderForm = getValues();
      const customerDocRef = doc(db, 'customers', orderForm.customerRef);
      const newOrderDocRef = await addDoc(ordersDocRef, {
        ...orderForm,
        customerRef: customerDocRef,
        dateCreated: parseDateTime(orderForm.dateCreated),
        dateUpdated: '',
        dateShipping: '',
        dateCompleted: '',
        userCreated: auth.currentUser.displayName,
      }).then((orderDocRef) => {
        setStateForm('edit');
        setStateSaving(false);
        if (dispatch) {
          dispatch({
            type: Actions.Snackbar,
            payload: { open: true, severity: 'success', message: `Successfully added order: ${orderDocRef.id}` },
          });
          dispatch({
            type: Actions.FormOrder,
            payload: orderDocRef.id,
          });
          dispatch({
            type: Actions.TabsOrder,
            payload: 'products',
          });
        }
      });
    } catch (err) {
      setStateSaving(false);
      dispatch &&
        dispatch({
          type: Actions.Snackbar,
          payload: { open: true, severity: 'error', message: `Error adding new order: ${err.message}` },
        });
    }
  };

  const saveOrder = async () => {
    try {
      setStateSaving(true);
      let orderForm = getValues();
      delete orderForm.dateCreated;
      delete orderForm.userCreated;
      // delete orderForm.dateUpdated;
      // delete orderForm.dateCompleted;
      const customerDocRef = doc(db, 'customers', orderForm.customerRef);
      await updateDoc(orderDocRef, {
        ...orderForm,
        customerRef: customerDocRef,
        dateUpdated: getNewDateTime(),
        dateCompleted:
          orderForm.orderStatus === 'completed' && orderForm.dateCompleted === ''
            ? getNewDateTime()
            : parseDateTime(orderForm.dateCompleted),
        ...(orderForm.orderStatus !== 'completed' && {
          // dateUpdated: getNewDate(),
          userUpdated: auth.currentUser.displayName,
        }),
        ...(orderForm.orderStatus === 'completed' && {
          // dateCompleted: getNewDate(),
          userCompleted: auth.currentUser.displayName,
        }),
        ...(orderForm.orderStatus === 'shipping' && {
          dateShipping: parseDate(orderForm.dateShipping),
          userShipping: auth.currentUser.displayName,
        }),
      }).then(() => {
        setStateForm('edit');
        setStateSaving(false);
        dispatch &&
          dispatch({
            type: Actions.Snackbar,
            payload: {
              open: true,
              severity: 'success',
              message: `Successfully update order: ${orderForm.name}`,
            },
          });
      });
    } catch (err) {
      setStateSaving(false);
      dispatch &&
        dispatch({
          type: Actions.Snackbar,
          payload: {
            open: true,
            severity: 'error',
            message: `Error saving new order: ${err.message}`,
          },
        });
    }
  };

  const editOrder = () => {
    setStateForm('save');
  };

  const createTransaction = () => {
    // console.log('createTransaction', getValues('customerRef'));
    dispatch &&
      dispatch({
        type: Actions.FormCustomer,
        payload: getValues('customerRef'),
      });
    dispatch &&
      dispatch({
        type: Actions.DialogTransaction,
        payload: true,
      });
  };

  const revertOrder = () => {
    setStateForm('edit');
    fetchOrder().then();
  };

  const deleteOrder = async () => {
    const productsRef = query(collection(db, 'products'), where('orderRef', '==', orderDocRef));
    const productsSnapshot = await getCountFromServer(productsRef);
    if (productsSnapshot?.data().count === 0) {
      try {
        setStateSaving(true);
        await deleteDoc(orderDocRef).then(() => {
          setStateSaving(false);
          if (dispatch) {
            dispatch({
              type: Actions.DialogDelete,
              payload: {
                open: false,
                title: '',
                onConfirm: () => {},
              },
            });
            dispatch({
              type: Actions.DrawerOrder,
              payload: false,
            });
            dispatch({
              type: Actions.Snackbar,
              payload: {
                open: true,
                severity: 'success',
                message: 'Successfully order deleted.',
              },
            });
            dispatch({
              type: Actions.FormOrder,
              payload: '',
            });
          }
        });
      } catch (err) {
        setStateSaving(false);
        dispatch({
          type: Actions.Snackbar,
          payload: {
            open: true,
            severity: 'error',
            message: `Error deleting order: ${err}`,
          },
        });
      }
    } else {
      dispatch({
        type: Actions.DialogDelete,
        payload: {
          open: false,
          title: '',
          onConfirm: () => {},
        },
      });
      dispatch({
        type: Actions.Snackbar,
        payload: {
          open: true,
          severity: 'error',
          message: `Please first delete all products related to this order.`,
        },
      });
    }
  };

  const handleDialogDelete = () => {
    dispatch &&
      dispatch({
        type: Actions.DialogDelete,
        payload: { open: true, title: 'Are you sure you want to delete this Order?', onConfirm: deleteOrder },
      });
  };

  const handleDialogCustomer = () => {
    // console.log(getValues());
    dispatch &&
      dispatch({
        type: Actions.Forms,
        payload: {
          formCustomer: '',
          formCustomerPhone: '',
        },
      });
    dispatch &&
      dispatch({
        type: Actions.DialogCustomer,
        payload: true,
      });
  };

  useEffect(() => {
    setStateLoading(true);
    fetchOrder().then(() => {
      setStateLoading(false);
    });
  }, [stateFormOrder]);

  useEffect(() => {
    // console.log(stateForm);
  }, [stateForm]);

  useEffect(() => {
    const fetchTransactions = async () => {
      const q = query(collection(db, 'transactions'), where('orderRef', '==', orderDocRef));
      const transactionsSnapshot = await getDocs(q);
      setOrderTransaction(transactionsSnapshot.docs.length > 0);
    };
    if (orderDocRef) fetchTransactions().then();
  }, [orderDocRef]);

  useEffect(() => {
    // console.log('stateFormOrderCustomer', stateFormOrderCustomer);
    if (stateFormOrderCustomer) {
      setValue('customerRef', stateFormOrderCustomer);
      dispatch &&
        dispatch({
          type: Actions.FormOrderCustomer,
          payload: '',
        });
    }
  }, [stateFormOrderCustomer]);

  useEffect(() => {
    const fetchCustomerOrderStatus = async () => {
      // console.log('stateFormOrderCustomer', stateFormOrderCustomer);
      if (watchOrderCustomer) {
        setCustomerStatusLoading(true);
        const customerDocRef = doc(db, 'customers', watchOrderCustomer);
        const customerData = (await getDoc(customerDocRef)).data();
        setStateCustomerPhone(customerData?.phone);
        const customerOrdersRef = query(
          collection(db, 'orders'),
          where('customerRef', '==', customerDocRef),
          where('orderStatus', '==', 'unpaid'),
        );
        const ordersSnapshot = await getCountFromServer(customerOrdersRef);
        setCustomerStatusLoading(false);
        if (ordersSnapshot?.data().count > 0) {
          setCustomerStatus('unpaid');
        } else {
          setCustomerStatus(null);
        }
      } else {
        setCustomerStatus(null);
        setStateCustomerPhone(null);
      }
    };
    fetchCustomerOrderStatus().then();
  }, [watchOrderCustomer]);

  return (
    <Box component={'form'}>
      <>
        {stateLoading ? (
          <Grid
            container
            spacing={3}
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <SkeletonForm />
            </Grid>
          </Grid>
        ) : (
          <Grid
            container
            spacing={3}
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <Grid
                container
                spacing={3}
              >
                <Grid size={12}>
                  <Grid
                    container
                    spacing={3}
                    alignItems={'center'}
                  >
                    <Grid size={'grow'}>
                      {/*<FieldCustomer*/}
                      {/*  control={control}*/}
                      {/*  name={'customerRef'}*/}
                      {/*  label={'Customer'}*/}
                      {/*  rules={[{ required: 'Customer is required' }]}*/}
                      {/*  disabled={stateForm !== 'create'}*/}
                      {/*  disableClear={true}*/}
                      {/*  setValue={setValue}*/}
                      {/*/>*/}
                      <FieldCustomerAutocomplete
                        control={control}
                        name={'customerRef'}
                        label={'Customer'}
                        rules={[{ required: 'Customer is required' }]}
                        freeSolo={true}
                        disabled={stateForm === 'edit' || (stateForm === 'save' && !permissions?.orders?.delete)}
                        disableClear={true}
                      />
                    </Grid>
                    {stateForm === 'create' && permissions?.customers?.create && (
                      <>
                        {customerStatusLoading && (
                          <Grid size={'auto'}>
                            <CircularProgress
                              color={'primary'}
                              size={24}
                            />
                          </Grid>
                        )}
                        {customerStatus && <Grid size={'auto'}>{renderValueOrderStatusChip(customerStatus)}</Grid>}
                        <Grid size={'auto'}>
                          <Tooltip title={'Create new customer'}>
                            <IconButton
                              onClick={handleDialogCustomer}
                              size={'large'}
                              color={'primary'}
                            >
                              <PersonAddAlt1 />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </>
                    )}
                    {stateForm === 'edit' && stateCustomerPhone && (
                      <Grid size={'auto'}>
                        <IconButton
                          component={'a'}
                          href={`tel:${stateCustomerPhone}`}
                          color={'primary'}
                          size={'large'}
                        >
                          <Phone />
                        </IconButton>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <Grid size={12}>
                  <FieldCurrency
                    control={control}
                    name={'downPayment'}
                    label={'Down Payment'}
                    disabled={stateForm === 'edit'}
                  />
                </Grid>
                <Grid size={12}>
                  <FieldCurrency
                    control={control}
                    name={'total'}
                    label={'Total'}
                    disabled={true}
                  />
                </Grid>
                <Grid size={12}>
                  <FieldText
                    control={control}
                    name={'products'}
                    label={'Products'}
                    disabled={true}
                  />
                </Grid>
                <Grid size={12}>
                  <FieldSelect
                    control={control}
                    name={'orderStatus'}
                    options={orderStatuses}
                    label={'Status'}
                    rules={[{ required: 'Name is required' }]}
                    disabled={
                      stateForm === 'edit' ||
                      stateForm === 'create' ||
                      watchOrderStatus === 'new' ||
                      watchOrderStatus === 'processed'
                    }
                    disabledOptions={['new', 'processed', 'ready']}
                    setValue={setValue}
                  />
                </Grid>
                <Grid size={12}>
                  <FieldTextarea
                    control={control}
                    name={'comments'}
                    label={'Comments'}
                    disabled={stateForm === 'edit'}
                  />
                </Grid>
                <Grid size={12}>
                  <Grid
                    container
                    spacing={2}
                  >
                    <Grid size={6}>
                      <FieldDateTime
                        control={control}
                        name={'dateCreated'}
                        label={'Date Created'}
                        setValue={setValue}
                        disabled={true}
                        disableClear={true}
                        // disabled={stateForm === 'edit'}
                      />
                    </Grid>
                    <Grid size={6}>
                      <FieldText
                        control={control}
                        name={'userCreated'}
                        label={'User Created'}
                        disabled={true}
                        // disabled={stateForm === 'edit'}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid size={12}>
                  <Grid
                    container
                    spacing={2}
                  >
                    <Grid size={6}>
                      <FieldDateTime
                        control={control}
                        name={'dateUpdated'}
                        label={'Date Updated'}
                        setValue={setValue}
                        disabled={true}
                        disableClear={true}
                        // disabled={stateForm === 'edit'}
                      />
                    </Grid>
                    <Grid size={6}>
                      <FieldText
                        control={control}
                        name={'userUpdated'}
                        label={'User Updated'}
                        disabled={true}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid size={12}>
                  <Grid
                    container
                    spacing={2}
                  >
                    <Grid size={6}>
                      <FieldDate
                        control={control}
                        name={'dateShipping'}
                        label={'Date Shipping'}
                        setValue={setValue}
                        disabled={stateForm === 'edit' || stateForm === 'create' || watchOrderStatus !== 'shipping'}
                        disableClear={true}
                        // disabled={stateForm === 'edit'}
                      />
                    </Grid>
                    <Grid size={6}>
                      <FieldText
                        control={control}
                        name={'userShipping'}
                        label={'User Shipping'}
                        disabled={true}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid size={12}>
                  <Grid
                    container
                    spacing={2}
                  >
                    <Grid size={6}>
                      <FieldDateTime
                        control={control}
                        name={'dateCompleted'}
                        label={'Date Completed'}
                        setValue={setValue}
                        disabled={true}
                        disableClear={true}
                        // disabled={stateForm === 'edit'}
                      />
                    </Grid>
                    <Grid size={6}>
                      <FieldText
                        control={control}
                        name={'userCompleted'}
                        label={'User Completed'}
                        disabled={true}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}></Grid>
          </Grid>
        )}
        {portalRef.current &&
          createPortal(
            <>
              <Grid
                container
                spacing={3}
                justifyContent="flex-end"
              >
                {stateForm === 'edit' && permissions?.transactions?.create && stateUser.security && (
                  <Grid size={'auto'}>
                    <Button
                      color={'primary'}
                      variant={'text'}
                      startIcon={<Add />}
                      onClick={createTransaction}
                      size={isMobile ? 'small' : 'large'}
                      loading={stateSaving}
                      disabled={orderTransaction}
                    >
                      Add transaction
                    </Button>
                  </Grid>
                )}
                {stateForm === 'edit' && permissions?.orders?.update && (
                  <Grid size={'auto'}>
                    <Button
                      color={'primary'}
                      variant={'contained'}
                      startIcon={<Edit />}
                      onClick={editOrder}
                      size={isMobile ? 'small' : 'large'}
                      loading={stateSaving}
                    >
                      Edit
                    </Button>
                  </Grid>
                )}
                {stateForm === 'save' && permissions?.orders?.update && (
                  <Grid size={'auto'}>
                    <Button
                      color={'primary'}
                      variant={'text'}
                      startIcon={<Undo />}
                      onClick={revertOrder}
                      size={isMobile ? 'small' : 'large'}
                      disabled={stateSaving}
                    >
                      Undo
                    </Button>
                  </Grid>
                )}
                {stateForm === 'save' && permissions?.orders?.delete && (
                  <Grid size={'auto'}>
                    <Button
                      color={'primary'}
                      variant={'text'}
                      startIcon={<Delete />}
                      onClick={handleDialogDelete}
                      size={isMobile ? 'small' : 'large'}
                      disabled={stateSaving}
                    >
                      Delete
                    </Button>
                  </Grid>
                )}
                {stateForm === 'save' && permissions?.orders?.create && (
                  <Grid size={'auto'}>
                    <Button
                      color={'primary'}
                      variant={'contained'}
                      startIcon={<Save />}
                      onClick={saveOrder}
                      size={isMobile ? 'small' : 'large'}
                      loading={stateSaving}
                    >
                      Save
                    </Button>
                  </Grid>
                )}
                {stateForm === 'create' && (
                  <Grid size={'auto'}>
                    <Button
                      color={'primary'}
                      variant={'contained'}
                      startIcon={<Add />}
                      onClick={addOrder}
                      size={isMobile ? 'small' : 'large'}
                      loading={stateSaving}
                    >
                      Create
                    </Button>
                  </Grid>
                )}
              </Grid>
            </>,
            portalRef.current,
          )}
      </>
    </Box>
  );
};

export default FormOrder;
